import {
  get,
  includes,
  isEmpty,
  isEqual,
  isPlainObject,
  isString,
  omit,
  pick,
  set,
  some,
} from 'lodash';
import {
  acceptedMediaTypes,
  getGcfComponentConfig,
  VERSIONS,
} from 'src/constants';
import {
  type IComponent,
  type IGcfProductListGroups,
  type ProjectConfigInterface,
  ComponentConfigImageFields,
  ComponentTypes,
  ICCAsset,
  IGcfComponentConfig,
  IGcfProductListProduct,
  IGcfProductListProducts,
  IProduct,
} from 'src/interfaces';
import { getKeyPaths } from 'src/utils';

import { DyProductSource } from './forms/singleShelf/SingleShelf.interface';

const INFO_BOX_DISPLAYABLE_COMPONENTS = [ComponentTypes.COD];

export const extractComponentAssetsName = (
  config?: Partial<IGcfComponentConfig>,
  assetFields: string[] = ComponentConfigImageFields,
) => {
  const paths = assetFields.flatMap((item) => getKeyPaths(config, item));

  const pickedConfig = pick(config, paths);

  const extensionsPattern = acceptedMediaTypes.join('|');
  const regex = new RegExp(`"([^"]+(${extensionsPattern}))"`, 'g');
  const matches = JSON.stringify(pickedConfig)?.match(regex);
  if (matches) {
    const fileNames = matches
      .map((match) => match.replace(/"/g, ''))
      .filter(
        (fileName) =>
          /^[^\\/:*?"<>|]+$/.test(fileName) &&
          !/^-?\d*\.?\d*px$/.test(fileName),
      );
    const uniqueFileNames = new Set(fileNames);

    return Array.from(uniqueFileNames);
  }

  return [];
};

export const isDy = (productsSource?: DyProductSource) =>
  !!productsSource && productsSource in DyProductSource;

export const shouldDisplayInfoBox = (
  componentType?: ComponentTypes,
  productsSource?: DyProductSource,
) => {
  return (
    includes(INFO_BOX_DISPLAYABLE_COMPONENTS, componentType) ||
    isDy(productsSource)
  );
};

export const formatComponentDetails = (
  componentDetails = {} as IComponent,
  projectConfig = {} as Partial<ProjectConfigInterface>,
) => {
  const cloneComponentDetails = structuredClone(componentDetails);

  /* Set default config if it is empty */
  if (isEmpty(cloneComponentDetails?.config)) {
    set(
      cloneComponentDetails,
      'config',
      getGcfComponentConfig(cloneComponentDetails?.component_type?.name),
    );
  }

  /* Set version in config */
  if (!get(cloneComponentDetails, 'config.version')) {
    set(
      cloneComponentDetails,
      'config.version',
      get(projectConfig, 'gcf.apps.components.version') === 'develop'
        ? VERSIONS.GCF_COMPONENTS_RELEASE_25
        : get(projectConfig, 'gcf.apps.components.version'),
    );
  }

  /* For LVG, set the first column if it is empty */
  if (
    cloneComponentDetails.component_type?.name ===
      ComponentTypes.LISTING_VISUALS_GALLERY &&
    !get(cloneComponentDetails, 'config.lineup') &&
    isEmpty(cloneComponentDetails?.config?.productList?.groups)
  ) {
    set(cloneComponentDetails, 'config.productList.groups', {
      '1': {
        order: 1,
        backups: {},
        products: {},
        config: {
          columnOrder: 1,
        },
      },
    });
  }

  /* For Combo, if pill visible is undefined, set to false by default */
  if (
    cloneComponentDetails.component_type?.name === ComponentTypes.COMBO_STYLE
  ) {
    const groups: IGcfProductListGroups = get(
      cloneComponentDetails,
      'config.productList.groups',
      {},
    );

    for (const groupKey in groups) {
      const group = groups[groupKey];

      if (get(group, 'config.groupHeader.pill.visible') === undefined) {
        set(group, 'config.groupHeader.pill.visible', false);
      }
    }
  }

  return cloneComponentDetails;
};

export const cleanupComponentLineup = async (
  componentConfig: Partial<IGcfComponentConfig>,
  products: IProduct[],
): Promise<boolean> => {
  let hasRemovedProducts = false;
  let hasRemovedBackupProducts = false;

  if (products.length > 0) {
    const groups = componentConfig?.productList?.groups;

    for (const groupKey in groups) {
      const group = groups[groupKey];

      // Check at products
      hasRemovedProducts = await checkDeletedProducts(group.products, products);

      // Check at backup products
      hasRemovedBackupProducts = await checkDeletedProducts(
        group.backups,
        products,
      );
    }
  }

  return hasRemovedProducts || hasRemovedBackupProducts;
};

export const checkDeletedProducts = (
  componentProducts: IGcfProductListProducts,
  products: IProduct[],
): Promise<boolean> => {
  return new Promise((resolve) => {
    let hasRemovedProducts = false;

    for (const productKey in componentProducts) {
      const product: IGcfProductListProduct = componentProducts[productKey];

      const currentProduct = products.find(
        (p) => p.product_code === product.productCode,
      );

      const productVariationCodes = new Set(
        currentProduct?.productVariations.map(
          (product) => product.product_code,
        ),
      );

      if (!currentProduct) {
        hasRemovedProducts = true;
        delete componentProducts[productKey];
      } else {
        for (const sizeKey in product.sizes) {
          const size = product.sizes[sizeKey];

          for (const variationsKey in size.variations) {
            const variation = size.variations[variationsKey];

            const found = productVariationCodes.has(variation.productCode);
            if (!found) {
              hasRemovedProducts = true;
              delete size.variations[variationsKey];
            }
          }
        }
      }
    }

    resolve(hasRemovedProducts);
  });
};

export const isComponentDetailsChanged = (
  originalComponentDetails?: IComponent,
  componentDetails?: IComponent,
  projectConfig?: Partial<ProjectConfigInterface>,
) => {
  // Omit version/config.version because it will be added when first editing the component
  // showPlaceholder is used only for CC previews and is removed before saving the component.
  const omitKeys = ['config.ads.showPlaceholder', 'config.version', 'version'];
  const omittedComponentDetails = omit(componentDetails, omitKeys);
  const omittedOriginalComponentDetails = omit(
    projectConfig
      ? formatComponentDetails(originalComponentDetails, projectConfig)
      : originalComponentDetails,
    omitKeys,
  );

  return (
    !!originalComponentDetails &&
    !!componentDetails &&
    !isEqual(
      // Make a deep copy of the object to avoid mutating the original object
      JSON.parse(JSON.stringify(omittedComponentDetails)),
      JSON.parse(JSON.stringify(omittedOriginalComponentDetails)),
    )
  );
};

export const findComponentAssets = (
  data?: Partial<IGcfComponentConfig>,
  ccAssets: ICCAsset[] = [],
) => {
  const componentAssetsName = extractComponentAssetsName(
    data,
    ComponentConfigImageFields,
  );

  return componentAssetsName.reduce(
    (acc, assetName) => {
      const foundAsset = ccAssets.find((item) => item.name === assetName);
      if (foundAsset) {
        acc.assets.push(foundAsset);
      } else {
        acc.missingAssets.push(assetName);
      }

      return acc;
    },
    {
      assets: [],
      missingAssets: [],
    } as {
      assets: ICCAsset[];
      missingAssets: string[];
    },
  );
};

export const getAssetIdsByProductCodes = (
  products: IProduct[] = [],
  productVariationCodes: string[],
): number[] => {
  const assetIds: number[] = [];

  for (const product of products) {
    for (const variation of product?.productVariations ?? []) {
      if (productVariationCodes.includes(variation.product_code)) {
        assetIds.push(...(variation.asset_id ?? []));
      }
    }
  }

  return Array.from(new Set(assetIds));
};

// Functions for validating/sanitizing rich text inputs in cod components
export const extractTextFromHtml = (html: string) => {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    let text = (doc.body && doc.body.textContent) || '';
    text = text
      .replace(/\u00A0/g, ' ')
      .replace(/\u200B/g, '')
      .replace(/&nbsp;/gi, '')
      .trim();

    return text;
  } catch {
    return html
      .replace(/<(.|\n)*?>/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\u200B/g, '')
      .trim();
  }
};

export const sanitizeRichTextInObject = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') {
    if (obj.includes('<') && obj.includes('>')) {
      const text = extractTextFromHtml(obj);

      return text === '' ? '' : obj;
    }

    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeRichTextInObject(item));
  }
  if (typeof obj === 'object') {
    const out: any = {};
    for (const k of Object.keys(obj)) {
      out[k] = sanitizeRichTextInObject(obj[k]);
    }

    return out;
  }

  return obj;
};

export const hasEmptyMessage = (obj: any): boolean =>
  some(obj, (value: any, key: string) => {
    if (key === 'message' && value === '') return true;
    if (isString(value) || value == null) return false;
    if (isPlainObject(value) || Array.isArray(value))
      return hasEmptyMessage(value);

    return false;
  });

export const isNonEmptyString = (val?: string) => !!val?.trim();
